import {
  bindTreatmentPatient,
  createTreatmentPreviewSession,
  createTreatmentSession,
  getTreatmentSession,
  isTerminalTreatmentSessionError,
  transitionTreatmentSession,
} from "./authorityApi";
import { buildAuthorityPayload } from "./authorityPayload";
import { previewPatientDefaults } from "./previewSimulation";
import {
  TREATMENT_RUNTIME_ACTION,
  TREATMENT_RUNTIME_STATE,
  TREATMENT_RUNTIME_STATE_ORDER,
} from "./treatmentApiConstants";
import {
  clearTreatmentPreviewSession,
  loadTreatmentPreviewSession,
  persistTreatmentPreviewSession,
} from "./previewSessionStorage";
import { authorityStateForQuestion } from "./authorityProgress";
import { hasSelectedLibraryConsentAnswer } from "./consentAuthorityContract";
import { hasCompleteCommonBoundaryInput } from "./commonBoundaryInput";

const actionsByState = {
  routing: ({ serviceState, routing, profile, location }) => [
    TREATMENT_RUNTIME_ACTION.answerRouting,
    {
      service_state: serviceState,
      answers: routing,
      preview_patient: profile,
      location,
    },
  ],
  potential_matches: ({ selectedPrograms }) => [
    TREATMENT_RUNTIME_ACTION.selectTreatments,
    { program_ids: selectedPrograms },
  ],
  common_screening: ({ common }) => [
    TREATMENT_RUNTIME_ACTION.answerCommon,
    { answers: common },
  ],
  treatment_screening: ({ programs }) => [
    TREATMENT_RUNTIME_ACTION.answerPrograms,
    { answers: programs },
  ],
  consents: ({ consents }) => [
    TREATMENT_RUNTIME_ACTION.acceptConsents,
    { consents },
  ],
  recommendations: ({ recommendations }, session) => [
    TREATMENT_RUNTIME_ACTION.confirmRecommendations,
    {
      program_ids: (
        recommendations?.length
          ? recommendations
          : session?.matching?.recommended_programs || []
      ),
    },
  ],
  lab_selection: (payload) => [
    TREATMENT_RUNTIME_ACTION.selectLabs,
    {
      lab_selections: payload.labSelections || [],
      lab_checkout: payload.labCheckout || {},
    },
  ],
  shipping: ({ shipping }) => [
    TREATMENT_RUNTIME_ACTION.confirmShipping,
    { shipping },
  ],
  product_selection: (payload, session) => {
    const eligible = eligibleProductSelectionPayload(payload, session);
    const usesSeparateShipping = Number(
      session?.location_contract_version || 0,
    ) >= 4;
    return [
      TREATMENT_RUNTIME_ACTION.selectProducts,
      {
        selections: eligible.selections,
        lab_selections: eligible.labSelections,
        coupon_code: eligible.couponCode || "",
        ...(!usesSeparateShipping && eligible.shipping
          ? { shipping: eligible.shipping }
          : {}),
      },
    ];
  },
};

const selectionOptionId = (selection) => String(
  selection?.option_id || "",
);

export const eligibleProductSelectionPayload = (payload, session) => {
  const eligibleOptionIds = new Set(
    (session?.step?.programs || []).flatMap((program) => (
      (program.checkout_questions || []).flatMap((question) => (
        (question.options || []).map((option) => String(
          option.option_id || "",
        ))
      ))
    )).filter(Boolean),
  );
  return {
    ...payload,
    selections: (payload.selections || []).filter(
      (selection) => eligibleOptionIds.has(selectionOptionId(selection)),
    ),
  };
};

export const startTreatmentAuthority = ({
  sourceKind,
  sourceId,
  release,
  apiBaseUrl,
}) => {
  if (!sourceKind || !sourceId || !release?.public_token || !release?.version) {
    throw new Error("Published treatment release authority is unavailable.");
  }
  return createTreatmentSession(sourceKind, sourceId, release, apiBaseUrl);
};

export const startTreatmentPreviewAuthority = async (
  previewCapabilityToken,
  apiBaseUrl = null,
  {
    forceNew = false,
    snapshotId = null,
    snapshotChecksum = null,
    sourceKind = null,
    sourceId = null,
    previewPatient = null,
  } = {},
) => {
  let saved = null;
  if (!forceNew) {
    try {
      saved = loadTreatmentPreviewSession(
        previewCapabilityToken,
        {
          apiBaseUrl,
          snapshotId,
          snapshotChecksum,
          sourceKind,
          sourceId,
        },
      );
    } catch (error) {
      // A saved pointer that fails to load (invalid, or pinned to a
      // different preview snapshot) is not resumable. Treat it the same as
      // no saved session and fall through to issuing a fresh one, instead
      // of surfacing a "start over" notice for something the UI can just
      // recover from silently.
      clearTreatmentPreviewSession();
    }
  }
  const authorityApiBaseUrl = (
    saved?.runtime_api_base_url || apiBaseUrl || null
  );
  if (!forceNew && saved?.session_token) {
    try {
      const resumed = await getTreatmentSession(
        saved.session_token,
        previewCapabilityToken,
        authorityApiBaseUrl,
      );
      persistTreatmentPreviewSession(
        previewCapabilityToken,
        resumed,
      );
      return resumed;
    } catch (error) {
      if (
        typeof isTerminalTreatmentSessionError === "function"
        && isTerminalTreatmentSessionError(error)
      ) {
        clearTreatmentPreviewSession();
      } else {
        throw error;
      }
    }
  }
  if (forceNew) clearTreatmentPreviewSession();
  const startedAt = globalThis.performance?.now?.() ?? Date.now();
  const created = await createTreatmentPreviewSession(
    previewCapabilityToken,
    authorityApiBaseUrl,
    previewPatient,
  );
  if (process.env.NODE_ENV === "development") {
    const finishedAt = globalThis.performance?.now?.() ?? Date.now();
    console.debug("[TreatmentPreview] preview session created", {
      durationMs: Math.round(finishedAt - startedAt),
    });
  }
  persistTreatmentPreviewSession(
    previewCapabilityToken,
    created,
  );
  return created;
};

const terminalStateError = (state) => (
  state === TREATMENT_RUNTIME_STATE.disqualified
    ? "Your answers do not currently match an available treatment."
    : "This treatment session is no longer available."
);

const stateOrder = (state) => TREATMENT_RUNTIME_STATE_ORDER[state];

const failRevisionConflict = (error, prior) => {
  if (
    !["stale_revision", "runtime_revision_stale"].includes(error?.code)
    || !error.current
  ) {
    throw error;
  }
  error.code = "runtime_revision_stale";
  error.current = {
    ...error.current,
    ...(prior?.preview_capability_token
      ? { preview_capability_token: prior.preview_capability_token }
      : {}),
    ...(prior?.preview_api_base_url
      ? { preview_api_base_url: prior.preview_api_base_url }
      : {}),
    ...(prior?.runtime_api_base_url
      ? { runtime_api_base_url: prior.runtime_api_base_url }
      : {}),
  };
  error.reconciliationRequired = true;
  throw error;
};

const hasInputForBoundary = (state, payload, session) => {
  if (state === TREATMENT_RUNTIME_STATE.potentialMatches) {
    return payload.selectedPrograms.length > 0;
  }
  if (state === TREATMENT_RUNTIME_STATE.commonScreening) {
    return hasCompleteCommonBoundaryInput(payload, session);
  }
  if (state === TREATMENT_RUNTIME_STATE.consents) {
    // A released, explicitly declined consent choice is terminal. Let the
    // server evaluate it even when other required consents are unanswered so
    // the authoritative disqualification projection wins over form errors.
    const hasDisqualifyingConsentSelection = Object.values(
      payload.consents || {},
    ).some((answer) => (
      answer
      && typeof answer === "object"
      && answer.accepted === false
      && answer.selected_value !== undefined
      && answer.selected_value !== null
      && answer.selected_value !== ""
    ));
    if (hasDisqualifyingConsentSelection) return true;
    const requiredConsents = (session?.step?.consents || []).filter(
      (consent) => consent.required !== false,
    );
    if (requiredConsents.length) {
      return requiredConsents.every((consent) => (
        hasSelectedLibraryConsentAnswer(
          payload.consents?.[String(consent.release_node_id)],
        )
      ));
    }
    const submitted = Object.values(payload.consents || {});
    return submitted.length > 0
      && submitted.every(hasSelectedLibraryConsentAnswer);
  }
  if (state === TREATMENT_RUNTIME_STATE.labSelection) {
    const checkout = payload.labCheckout;
    if (!checkout || typeof checkout !== "object" || !Object.keys(checkout).length) {
      return false;
    }
    const programLabs = (session?.step?.program_labs || []).flatMap((p) => p.labs || []);
    const hasRequiredLabs = programLabs.some((lab) => lab.is_required !== false);
    const hasSelections = (payload.labSelections || []).length > 0;
    if (hasRequiredLabs || hasSelections) {
      const address = checkout.address;
      const consents = checkout.consents;
      const hasAddress = Boolean(
        address
        && String(address.street || "").trim()
        && String(address.city || "").trim()
        && String(address.state || "").trim()
        && String(address.zip || "").trim()
      );
      const hasConsents = Boolean(
        consents?.hipaa === true
        && consents?.terms === true
        && consents?.telehealth === true
      );
      return hasAddress && hasConsents;
    }
    return true;
  }
  if (state === TREATMENT_RUNTIME_STATE.shipping) {
    const location = payload.shipping?.location;
    return Boolean(
      payload.shipping?.use_service_location === true
      || (
        location
        && String(location.street_1 || "").trim()
        && String(location.city || "").trim()
        && String(location.state || "").trim()
        && String(location.zip_code || "").trim()
      )
    );
  }
  if (state === TREATMENT_RUNTIME_STATE.productSelection) {
    const eligible = eligibleProductSelectionPayload(payload, session);
    return (
      eligible.selections.length > 0
      || eligible.labSelections.length > 0
    );
  }
  return true;
};

export const synchronizeTreatmentAuthority = async ({
  session,
  questionnaire,
  responses,
  patientInfo,
  patientAccessToken,
  targetState,
  couponCode,
  recommendedProgramIds = null,
  allowBackward = false,
  backTargetNodeId = null,
}) => {
  if (!session) throw new Error("Treatment session is unavailable.");
  if (
    session.mode !== "preview"
    && !session.patient_bound
    && targetState !== TREATMENT_RUNTIME_STATE.routing
    && !patientAccessToken
  ) {
    throw new Error("Please sign in again before checkout.");
  }
  if (
    allowBackward
    && stateOrder(targetState) > stateOrder(session.state)
  ) {
    // A stale authored predecessor can belong to a later runtime phase.
    // Back must still be a server Back, never submission of the current form.
    try {
      return await transitionTreatmentSession(
        session,
        TREATMENT_RUNTIME_ACTION.back,
        {},
      );
    } catch (error) {
      failRevisionConflict(error, session);
    }
  }
  const payload = buildAuthorityPayload(questionnaire, responses);
  if (payload.location) {
    payload.profile.location = payload.location;
  }
  const resolvedPreviewPatientInfo = session.mode === "preview"
    ? (patientInfo || previewPatientDefaults(questionnaire))
    : patientInfo;
  if (session.mode === "preview" && resolvedPreviewPatientInfo) {
    const hasAuthoredCareLocation = (questionnaire?.questions || []).some(
      (question) => question.question_type === "state_routing",
    );
    const previewState = String(
      resolvedPreviewPatientInfo.state
        || resolvedPreviewPatientInfo.service_state
        || "",
    ).trim().toUpperCase();
    payload.profile = {
      date_of_birth: resolvedPreviewPatientInfo.date_of_birth,
      sex: resolvedPreviewPatientInfo.sex,
      bmi: resolvedPreviewPatientInfo.bmi,
      service_state: (
        resolvedPreviewPatientInfo.state
        || resolvedPreviewPatientInfo.service_state
      ),
      ...payload.profile,
    };
    Object.keys(payload.profile).forEach((key) => {
      if (payload.profile[key] == null || payload.profile[key] === "") {
        delete payload.profile[key];
      }
    });
    // Care location remains an authored, explicitly confirmed boundary. When
    // it exists, the preview profile must use the same transition field as a
    // live patient; profile state alone is intentionally not visit authority.
    if (hasAuthoredCareLocation && !payload.serviceState && previewState) {
      payload.serviceState = previewState;
    }
  }
  payload.couponCode = couponCode || "";
  if (Array.isArray(recommendedProgramIds)) {
    // Continuing without a blocked sibling. This must be explicit: with no
    // recommendations-stage question rendered, payload.recommendations is
    // always empty and actionsByState.recommendations would otherwise fall
    // back to the session's full list, re-adding the treatment being dropped.
    payload.recommendations = recommendedProgramIds.map(String);
  }
  let current = session;
  if (
    session.mode !== "preview"
    && !session.patient_bound
    && targetState !== TREATMENT_RUNTIME_STATE.routing
  ) {
    try {
      current = await bindTreatmentPatient(
        session,
        patientAccessToken,
        payload.profile,
      );
    } catch (error) {
      failRevisionConflict(error, session);
    }
  }
  const seen = new Set();
  const backPayload = (
    allowBackward
    && targetState
    && targetState !== current.state
  )
    ? {
        back_target: {
          state: targetState,
          ...(backTargetNodeId ? { node_id: String(backTargetNodeId) } : {}),
        },
      }
    : {};
  if (allowBackward && current.state === targetState) {
    try {
      return await transitionTreatmentSession(
        current,
        TREATMENT_RUNTIME_ACTION.back,
        {},
      );
    } catch (error) {
      failRevisionConflict(error, current);
    }
  }
  while (current.state !== targetState) {
    if (seen.has(`${current.state}:${current.session_revision}`)) {
      throw new Error("Treatment session could not advance.");
    }
    seen.add(`${current.state}:${current.session_revision}`);
    if (
      allowBackward
      &&
      current.state === TREATMENT_RUNTIME_STATE.disqualified
      && current.back_state === targetState
    ) {
      try {
        current = await transitionTreatmentSession(
          current,
          TREATMENT_RUNTIME_ACTION.back,
          {},
        );
      } catch (error) {
        failRevisionConflict(error, current);
      }
      continue;
    }
    if (current.state === TREATMENT_RUNTIME_STATE.disqualified) {
      return current;
    }
    if (
      [
        TREATMENT_RUNTIME_STATE.expired,
        TREATMENT_RUNTIME_STATE.completed,
        TREATMENT_RUNTIME_STATE.checkoutConsumed,
      ].includes(current.state)
    ) {
      return current;
    }
    const currentOrder = stateOrder(current.state);
    const targetOrder = stateOrder(targetState);
    if (currentOrder === undefined || targetOrder === undefined) {
      throw new Error(`Unsupported treatment state: ${current.state}`);
    }
    if (currentOrder > targetOrder) {
      // A forward synchronization must never silently rewind a live journey.
      // Back is an explicit user intent and is sent by the UI with
      // allowBackward=true.  Without this guard, a stale response or a
      // consent/checkpoint retry could send the patient back to an earlier
      // boundary and recreate the reported "loop".
      if (!allowBackward) return current;
      try {
        current = await transitionTreatmentSession(
          current,
          TREATMENT_RUNTIME_ACTION.back,
          backPayload,
        );
      } catch (error) {
        failRevisionConflict(error, current);
      }
      continue;
    }
    // The server can skip an empty boundary while rewinding. Stop at that
    // earlier cursor; replaying answers to reach the requested state creates
    // a Back/Continue loop and validates unanswered consent on Back.
    if (allowBackward) return current;
    if (!hasInputForBoundary(current.state, payload, current)) {
      return current;
    }
    const transition = actionsByState[current.state]?.(payload, current);
    if (!transition) throw new Error(`Unsupported treatment state: ${current.state}`);
    try {
      current = await transitionTreatmentSession(
        current,
        transition[0],
        transition[1],
      );
    } catch (error) {
      failRevisionConflict(error, current);
    }
    if (
      stateOrder(current.state) > targetOrder
      && current.state !== TREATMENT_RUNTIME_STATE.disqualified
    ) {
      return current;
    }
  }
  if (
    backPayload.back_target?.node_id
    && current.cursor_node_id !== backPayload.back_target.node_id
  ) {
    throw new Error("Unable to return to the previous treatment question.");
  }
  return current;
};

/**
 * Place runtime authority on the Program screening boundary before saving an
 * individual screening answer.
 *
 * A released Program is allowed to have no authored routing questions, but a
 * new backend session still begins in the internal `routing` state. The UI
 * intentionally does not render a synthetic routing step in that case. This
 * helper advances that invisible boundary before the answer checkpoint so the
 * server never receives `checkpoint_program_answers` from `routing`.
 */
export const synchronizeProgramCheckpointBoundary = async (options) => {
  if (!options?.session) {
    throw new Error("Treatment session is unavailable.");
  }
  if (
    options.session.state === TREATMENT_RUNTIME_STATE.treatmentScreening
  ) {
    return options.session;
  }
  return synchronizeTreatmentAuthority({
    ...options,
    targetState: TREATMENT_RUNTIME_STATE.treatmentScreening,
  });
};

/**
 * Align a runtime session with the authoritative boundary that owns a
 * question before performing a question-scoped server mutation such as a
 * multipart upload. The backend remains fail-closed if the question is not
 * available in that state.
 */
export const synchronizeQuestionBoundary = async (options, question) => {
  if (!options?.session) {
    throw new Error("Treatment session is unavailable.");
  }
  const targetState = authorityStateForQuestion(question);
  if (!targetState) {
    throw new Error("The question does not have a runtime boundary.");
  }
  if (options.session.state === targetState) {
    return options.session;
  }
  return synchronizeTreatmentAuthority({
    ...options,
    targetState,
  });
};

export { continueWithEligiblePrograms } from "./eligibleProgramContinuation";

export const prepareTreatmentCheckout = async (options) => {
  const current = await synchronizeTreatmentAuthority({
    ...options,
    targetState: TREATMENT_RUNTIME_STATE.checkoutReady,
  });
  if (
    [
      TREATMENT_RUNTIME_STATE.disqualified,
      TREATMENT_RUNTIME_STATE.expired,
      TREATMENT_RUNTIME_STATE.completed,
      TREATMENT_RUNTIME_STATE.checkoutConsumed,
    ].includes(current.state)
  ) {
    throw new Error(terminalStateError(current.state));
  }
  if (current.state === TREATMENT_RUNTIME_STATE.productSelection) {
    throw new Error(
      "Select a treatment option before continuing to checkout.",
    );
  }
  if (current.state !== TREATMENT_RUNTIME_STATE.checkoutReady) {
    throw new Error(
      "Checkout could not be prepared from the current treatment step.",
    );
  }
  if (
    !Array.isArray(current.treatments)
    || current.treatments.length === 0
  ) {
    throw new Error(
      "Select a treatment option before continuing to checkout.",
    );
  }
  if (!current.checkout_capability) {
    if (current.mode !== "preview") {
      throw new Error("Secure checkout authority was not issued.");
    }
  }
  return current;
};
